import { SimpleCarousel } from '@eventbrite/collection-carousel';
import { track } from '@eventbrite/datalayer-library';
import { Icon } from '@eventbrite/eds-icon';
import { ArrowRightChunky } from '@eventbrite/eds-iconography';
import {
    EventCardShareAction,
    FormattedEvent,
} from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import { LoadingSkeleton } from '@eventbrite/loading-skeleton';
import React from 'react';
import { connect } from 'react-redux';
import DiscoverVerticalBrowseHierarchyEventCard from '../../../../components/DiscoverEventCard/Vertical/DiscoverVerticalBrowseHierarchyEventCard';
import {
    GetEnrollmentStatus,
    useGetStatsigEnrollment,
} from '../../../../hooks';
import {
    ConnectedProps,
    DiscoverState,
    TodayCarouselExperiment,
    TodayCarouselExperimentValues,
} from '../../../../types/index';
import { HOMEPAGE_TODAY_CAROUSEL, UtmOptions } from '../../constants/analytics';
import { useCategoryEventsQuery } from '../../hooks/events/events';
import { clickOnViewEvent } from '../../redux/events';
import './TodayCarouselContainer.scss';

interface Event extends FormattedEvent {
    hideStartDate?: boolean;
    numFollowers?: number;
    venueName?: string;
    venueAddress?: string;
}

const utmOptions: UtmOptions = {
    'utm-campaign': 'social',
    'utm-content': 'attendeeshare',
    'utm-medium': 'discovery',
    'utm-term': 'listing',
    'utm-source': 'cp',
};

const handleViewMoreClick = () => {
    const todayTab = document.querySelector('[data-tab-key="today"]');
    if (todayTab) {
        const todayButton = todayTab.querySelector('button');
        if (todayButton) {
            todayButton.click();
        }
    }
};

const TitleDescription = () => (
    <div className="title-subtitle-container">
        <h2>{gettext('Today')}</h2>
        <div className="view-more-desktop" onClick={handleViewMoreClick}>
            {gettext('View more')}
        </div>
    </div>
);

export const TodayCarousel = ({
    isAuthenticated,
    isMobile,
    location,
    onViewEvent,
    shareOptions,
}: ReduxProps) => {
    React.useEffect(() => {
        if (location && location.currentPlace) {
            track({
                eventName: 'TodayCarouselView',
                eventData: {
                    searchLocation: location.currentPlace,
                },
            });
        }
    }, [location]);

    const todayCarouselExperiment = useGetStatsigEnrollment({
        name: TodayCarouselExperiment.name,
        paramName: TodayCarouselExperiment.paramName,
        defaultValue: TodayCarouselExperiment.values.control,
    });

    const todayCarouselVariant =
        todayCarouselExperiment.status === GetEnrollmentStatus.SUCCESS
            ? todayCarouselExperiment.variant
            : GetEnrollmentStatus.LOADING;

    const { data, isLoading } = useCategoryEventsQuery({
        queryOverload: {
            page_size: 10,
            aggs: {},
            dates: ['current_future', 'today'],
        },
        refetchOnMount: false,
        location: location,
        enabled: true,
    });

    if (isLoading || todayCarouselVariant === GetEnrollmentStatus.LOADING) {
        return (
            <div className="loader">
                <LoadingSkeleton height={isMobile ? '400px' : '322px'} />
            </div>
        );
    }

    if (todayCarouselVariant === TodayCarouselExperimentValues.control) {
        return null;
    }

    if (!data || data?.events.length == 0) return null;
    const events: Event[] = data?.events || [];
    const showNavigation = events.length > 2;
    let cardsComponent: React.ReactElement[] = [];

    // Custom bucket key for tracking analytics
    const bucketKey = 'today_carousel_events';

    cardsComponent = events.map(
        ({
            id,
            isFree,
            isSoldOut,
            startDate,
            hideStartDate,
            numFollowers,
            ...cardProps
        }) => {
            return (
                <div className="today-carousel-event-card" key={id}>
                    <DiscoverVerticalBrowseHierarchyEventCard
                        event={{
                            ...cardProps,
                            id: id || '',
                            isFree: isFree || false,
                            isSoldOut: isSoldOut,
                            startDate: hideStartDate ? '' : startDate,
                            organizerFollowerCount: numFollowers,
                        }}
                        onClick={onViewEvent?.bind(
                            null,
                            id,
                            bucketKey,
                            numFollowers,
                        )}
                        moreActions={
                            <EventCardShareAction
                                shareOptions={{
                                    ...shareOptions,
                                    eventId: id,
                                    eventName: cardProps.name,
                                    eventUrl: cardProps.url,
                                    utmOptions,
                                    trackingCategory: 'eventcard',
                                }}
                            />
                        }
                        isAuthenticated={isAuthenticated || false}
                        affCode="ehometext"
                        isLazyImage={true}
                        statsigLocationString={HOMEPAGE_TODAY_CAROUSEL}
                        locale="en_US"
                    />
                </div>
            );
        },
    );

    return (
        <div className="today-carousel">
            <div className="today-carousel-container">
                <TitleDescription />
                <div className="simple-carousel-parent">
                    <SimpleCarousel
                        useSideNavigation={showNavigation}
                        enableSecondarySideNavIcons={true}
                    >
                        {cardsComponent}
                    </SimpleCarousel>
                </div>
                <div className="view-more" onClick={handleViewMoreClick}>
                    {gettext('View more')} <Icon type={<ArrowRightChunky />} />
                </div>
            </div>
        </div>
    );
};

const _mapStateToProps = ({
    app,
    user,
    location,
    shareOptions,
}: DiscoverState) => {
    return {
        isMobile: app.isMobile,
        isAuthenticated: user.isAuthenticated,
        location: location,
        shareOptions: shareOptions,
    };
};

const _mapDispatchToProps = {
    onViewEvent: clickOnViewEvent,
};
type ReduxProps = ConnectedProps<
    typeof _mapStateToProps,
    typeof _mapDispatchToProps
>;
export default connect(_mapStateToProps, _mapDispatchToProps)(TodayCarousel);
